import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import LucideIcon from "@/components/LucideIcon";
import { useApp } from "@/contexts/AppContext";
import { SCENARIO } from "@/constants/testIds";

/**
 * Bento scenario card with trigger -> action visual.
 */
export default function ScenarioCard({ scenario, big = false }) {
  const { localized, t } = useApp();

  return (
    <Link
      to={`/scenarios/${scenario.slug}`}
      data-testid={SCENARIO.card(scenario.slug)}
      className={`group relative block overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0B0F19] hover:border-white/15 transition-all duration-500 ${
        big ? "min-h-[420px]" : "min-h-[260px]"
      }`}
      style={{
        background: `radial-gradient(120% 80% at 0% 0%, ${scenario.color}10, transparent 55%), #0B0F19`,
      }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 transition-opacity duration-700 group-hover:opacity-90"
        style={{
          backgroundImage: `url(${scenario.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(0.95) contrast(1.05)",
          opacity: 0.7,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/60 to-[#0B0F19]/10" />

      <div className="relative h-full p-6 sm:p-8 flex flex-col">
        {/* Icon */}
        <div
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl border"
          style={{
            borderColor: `${scenario.color}55`,
            background: `${scenario.color}18`,
            color: scenario.color,
          }}
        >
          <LucideIcon name={scenario.icon} className="h-5 w-5" strokeWidth={2} />
        </div>

        <div className="mt-auto">
          <span
            className="inline-block text-[10px] tracking-[0.25em] uppercase font-bold mb-3"
            style={{ color: scenario.color }}
          >
            {localized(scenario, "tagline")}
          </span>
          <h3
            className={`font-heading font-medium leading-[1.05] tracking-tight text-white ${
              big ? "text-3xl sm:text-4xl" : "text-2xl"
            }`}
          >
            {localized(scenario, "title")}
          </h3>

          {/* trigger -> action flow */}
          <motion.div
            className="mt-6 flex items-center gap-2 flex-wrap"
            initial={false}
          >
            <Pill icon={scenario.trigger.icon} label={localized(scenario.trigger, "label")} color={scenario.color} />
            <div className="relative h-px w-8 bg-white/15 flex-shrink-0">
              <motion.div
                className="absolute inset-0 origin-left"
                style={{ background: scenario.color }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
              />
            </div>
            <Pill icon={scenario.action.icon} label={localized(scenario.action, "label")} color={scenario.color} solid />
          </motion.div>

          <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-gray-300 group-hover:text-white transition-colors">
            <span>{t.scenario.explore}</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function Pill({ icon, label, color, solid = false }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] sm:text-xs border"
      style={{
        borderColor: solid ? color : "rgba(255,255,255,0.1)",
        background: solid ? `${color}22` : "rgba(255,255,255,0.03)",
        color: solid ? color : "#D1D5DB",
      }}
    >
      <LucideIcon name={icon} className="h-3 w-3" strokeWidth={2.2} />
      <span className="truncate max-w-[140px]">{label}</span>
    </div>
  );
}
